'use client'

import { useEffect, useMemo, useState } from 'react'
import { Logo } from '@/shared/logo'
import {
  InstagramHeaderWhiteIcon,
  PhoneIcon,
  PlanetIcon,
  TelegramHeaderWhiteIcon,
  WhatsappHeaderWhiteIcon,
} from '@/shared/icons'
import { Link, LOCALES, SlugLink, usePathname } from '@/shared/i18n'
import { useHeaderContext } from './HeaderContext'
import { GetLanguageMenuSettingsDto } from '@/shared/api/strapi/requests'
import { useLocale } from 'use-intl'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/shared/ui'
import { ChevronRight, Menu } from 'lucide-react'
import { parsePhoneNumber } from '@/shared/lib/utils'
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from '@/shared/ui/drawer'
import { useMedia } from '@/shared/lib'
import { SearchInput } from '@/widgets/Header/SearchInput'
import { Article, Service, Specialist } from '@/shared/api/strapi/requests/search'

export interface HeaderProps {
  settings: GetLanguageMenuSettingsDto
  searchData: {
    specialists: Specialist[] | null
    news: Article[] | null
    services: Service[] | null
  }
}

export const Header = ({ settings: { header, dataSettings }, searchData }: HeaderProps) => {
  const [invertColors, setInvertColors] = useState(false)

  const locale = useLocale()
  const pathname = usePathname()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { localizedSlugs } = useHeaderContext()

  const shouldInvertColors = useMemo(() => pathname === '/', [pathname])
  const isMobile = useMedia('(max-width: 767px)')

  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  useEffect(() => {
    const handleScroll = () => {
      setInvertColors(window.scrollY >= 93)
    }
    const ctrl = new AbortController()
    window.addEventListener('scroll', () => handleScroll(), { passive: true, signal: ctrl.signal })
    return () => ctrl.abort()
  }, [shouldInvertColors])

  return (
    <header
      className={`h-header max-h-header z-10 sticky top-0 w-full py-4 px-8 
      ${!shouldInvertColors || invertColors ? 'text-black backdrop-blur-lg bg-white/55' : 'text-white'} transition-colors`}
    >
      <div className="grid grid-cols-[1fr_auto_1fr] gap-x-4 mb-5 md:mb-0">
        {isMobile && (
          <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
            <DrawerTrigger className="self-center justify-self-start">
              <Menu />
              <span className="sr-only">Menu</span>
            </DrawerTrigger>
            <DrawerContent aria-description={'Site navigation'} aria-describedby={undefined}>
              <DrawerTitle hidden>Site navigation</DrawerTitle>
              <div className={'flex flex-col items-center gap-12 px-8 py-6'}>
                <div className="w-full col-start-3 row-start-2 flex flex-row content-center justify-between gap-2 max-w-sm">
                  <Link href={dataSettings.instagram}>
                    <InstagramHeaderWhiteIcon className={'stroke-current fill-current'} />
                    <span className="sr-only">Instagram link in drawer</span>
                  </Link>
                  <Link href={dataSettings.telegram}>
                    <TelegramHeaderWhiteIcon className={'fill-current'} />
                    <span className="sr-only">Telegram link in drawer</span>
                  </Link>
                  <Link href={dataSettings.whatsapp}>
                    <WhatsappHeaderWhiteIcon className={'h-6 fill-current'} />
                    <span className="sr-only">Whatsapp link in drawer</span>
                  </Link>
                  <Link href={`tel:${parsePhoneNumber(dataSettings.phone)}`}>
                    <PhoneIcon className={'fill-current h-6'} />
                    <span className="sr-only">Telephone link in drawer</span>
                  </Link>
                  <DropdownMenu>
                    <DropdownMenuTrigger className={'flex flex-row content-center justify-start gap-1'}>
                      <PlanetIcon className={'fill-current'} />
                      {locale.toUpperCase()}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuRadioGroup value={locale}>
                        {LOCALES.map(itemLocale => {
                          if (itemLocale === locale) {
                            return (
                              <DropdownMenuRadioItem key={itemLocale} disabled={true} value={itemLocale}>
                                {itemLocale.toUpperCase()}
                              </DropdownMenuRadioItem>
                            )
                          }
                          const localizedSlug = localizedSlugs.find(slug => slug.attributes.locale === itemLocale)
                          return localizedSlug ? (
                            <SlugLink
                              locale={itemLocale}
                              key={itemLocale}
                              baseHref={pathname.split('/').slice(0, -1).join('/')}
                              slug={localizedSlug.attributes.slug}
                            >
                              <DropdownMenuRadioItem value={itemLocale}>
                                {itemLocale.toUpperCase()}
                              </DropdownMenuRadioItem>
                            </SlugLink>
                          ) : (
                            <Link key={itemLocale} locale={itemLocale} href={pathname}>
                              <DropdownMenuRadioItem value={itemLocale}>
                                {itemLocale.toUpperCase()}
                              </DropdownMenuRadioItem>
                            </Link>
                          )
                        })}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <nav className={'w-full flex flex-col gap-2'}>
                  {header.map(item => (
                    <Link
                      locale={locale}
                      key={item.id}
                      href={item.Href}
                      target={item?.Target_blank ? '_blank' : '_self'}
                    >
                      <div className={'flex flex-row justify-between items-center'}>
                        <p className={'text-lg text-nowrap'}>{item.Title}</p>
                        <ChevronRight className={'stroke-grey'} />
                      </div>
                    </Link>
                  ))}
                </nav>
              </div>
            </DrawerContent>
          </Drawer>
        )}

        <Link href={{ pathname: '/' }} className="justify-self-start">
          <Logo className={'h-[49px] fill-current'} />
          <span className="sr-only">Home</span>
        </Link>

        <nav className={'self-start hidden md:flex md:gap-4 lg:gap-11'}>
          {header.map(item => (
            <Link locale={locale} key={item.id} href={item.Href} target={item?.Target_blank ? '_blank' : '_self'}>
              <p className={'text-nowrap font-normal'}>{item.Title}</p>
            </Link>
          ))}
        </nav>

        <div className="self-center md:self-start flex flex-row-reverse md:flex-row md:justify-end gap-5 md:gap-2">
          <Link href={dataSettings.instagram} className={'md:inline'}>
            <InstagramHeaderWhiteIcon className={'stroke-current fill-current'} />
            <span className="sr-only">Instagram</span>
          </Link>
          <Link href={dataSettings.telegram} className={'hidden md:inline'}>
            <TelegramHeaderWhiteIcon className={'fill-current'} />
            <span className="sr-only">Telegram</span>
          </Link>
          <Link href={dataSettings.whatsapp}>
            <WhatsappHeaderWhiteIcon className={'h-6 fill-current'} />
            <span className="sr-only">Whatsapp</span>
          </Link>
          <Link href={`tel:${parsePhoneNumber(dataSettings.phone)}`} className={'md:hidden'}>
            <PhoneIcon className={'fill-current h-6'} />
            <span className="sr-only">Telephone</span>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger className={'hidden md:flex flex-row content-center justify-start gap-1'}>
              <PlanetIcon className={'fill-current'} />
              {locale.toUpperCase()}
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuRadioGroup value={locale}>
                {LOCALES.map(itemLocale => {
                  if (itemLocale === locale) {
                    return (
                      <DropdownMenuRadioItem key={itemLocale} disabled={true} value={itemLocale}>
                        {itemLocale.toUpperCase()}
                      </DropdownMenuRadioItem>
                    )
                  }
                  const localizedSlug = localizedSlugs.find(slug => slug.attributes.locale === itemLocale)
                  return localizedSlug ? (
                    <SlugLink
                      locale={itemLocale}
                      key={itemLocale}
                      baseHref={pathname.split('/').slice(0, -1).join('/')}
                      slug={localizedSlug.attributes.slug}
                    >
                      <DropdownMenuRadioItem value={itemLocale}>{itemLocale.toUpperCase()}</DropdownMenuRadioItem>
                    </SlugLink>
                  ) : (
                    <Link key={itemLocale} locale={itemLocale} href={pathname}>
                      <DropdownMenuRadioItem value={itemLocale}>{itemLocale.toUpperCase()}</DropdownMenuRadioItem>
                    </Link>
                  )
                })}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="m-auto max-w-[338]">
        <SearchInput locale={locale} invertColors={!shouldInvertColors || invertColors} searchData={searchData} />
      </div>
    </header>
  )
}
